import React, { useEffect, useState } from "react";
import { Stack, Text, StackProps, Box } from "@chakra-ui/react";
import { take } from "ramda";

import { getPhotoSrc, IPhoto, IVideo } from "utils/offer";
import useViewport from "hooks/useViewport";
import { isPhotoType, ReactFCC } from "src/types/types";
import CustomImage from "../CustomImage";
import IconPlay from "../Icons/Play";

const StyledMiniatureButton: ReactFCC<{
  isVideo?: boolean;
  index?: number;
  isActive?: boolean;
  onClick?: StackProps["onClick"];
}> = ({ isVideo = false, index, isActive, onClick, children }) => (
  <Stack
    boxSize="50px"
    bg="white"
    border="1px"
    borderColor={isActive ? "ud-primary" : "white"}
    borderRadius="5px"
    onClick={onClick}
    _hover={{ cursor: "pointer" }}
    position="relative"
    spacing={0}
  >
    {isVideo && index === 0 && (
      <Box
        cursor="pointer"
        position="absolute"
        zIndex={999}
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
        backgroundColor="pink"
        borderRadius="4px"
        w="20px"
        h="20px"
        onClick={onClick}
      >
        <IconPlay
          fill="ud-white"
          boxSize="12px"
          display="flex"
          margin="auto"
          h="100%"
        />
      </Box>
    )}
    {children}
  </Stack>
);

const GalleryMiniatures: React.FC<{
  media: Array<IPhoto | IVideo>;
  introPhoto: IPhoto;
  onClick: (index: number) => void;
  imageErrorFallback: string;
}> = ({ media, introPhoto, onClick, imageErrorFallback }) => {
  const [mediaPerLine, setMediaPerLine] = useState(5);
  const { isMobile } = useViewport();

  const lastMediaIndex = mediaPerLine - 1;

  const newMedia = React.useMemo<{
    moreMediaCount: number;
    media: Array<IPhoto | IVideo>;
  }>(
    () => ({
      moreMediaCount:
        media.length > mediaPerLine ? media.length - mediaPerLine : 0,
      media:
        media.length > mediaPerLine ? take(mediaPerLine - 1, media) : media,
    }),
    [media, mediaPerLine],
  );

  useEffect(() => {
    if (isMobile) {
      setMediaPerLine(3);
    }
  }, [isMobile]);

  const lastMedia = media[lastMediaIndex];

  return (
    <Stack direction="row" align="center">
      {newMedia.media.map((IMedia, idx) => (
        <StyledMiniatureButton
          key={idx}
          onClick={() => onClick(idx)}
          index={idx}
          isVideo={!isPhotoType(IMedia)}
        >
          <CustomImage
            src={
              isPhotoType(IMedia)
                ? getPhotoSrc(IMedia, imageErrorFallback)
                : getPhotoSrc(introPhoto, imageErrorFallback)
            }
            alt={IMedia.alt || ""}
            fill
            style={{
              objectFit: "cover",
              borderRadius: "5px",
            }}
          />
        </StyledMiniatureButton>
      ))}

      {newMedia.moreMediaCount !== 0 && (
        <StyledMiniatureButton
          onClick={() => {
            onClick(lastMediaIndex);
          }}
          isActive
        >
          <CustomImage
            src={
              isPhotoType(lastMedia)
                ? getPhotoSrc(lastMedia, imageErrorFallback)
                : getPhotoSrc(introPhoto, imageErrorFallback)
            }
            alt={media[lastMediaIndex]?.alt || ""}
            fill
            style={{
              objectFit: "cover",
              borderRadius: "5px",
              opacity: "0.3",
            }}
          />
          <Text
            position="absolute"
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="black"
            fontSize="0.9rem"
            fontWeight="bold"
            textAlign="center"
            marginTop="0 !important"
          >
            +{newMedia.moreMediaCount}
          </Text>
        </StyledMiniatureButton>
      )}
    </Stack>
  );
};

export default GalleryMiniatures;
